import { useMutation } from '@apollo/client';
import { useState } from 'react';

import { addCallNote, getCallById } from 'queries/calls';
import { addTask } from 'queries/tasks';

import { useUI } from 'providers/ui';

type SuggestedTask = {
  title: string;
  detail: string;
};

type Props = {
  callId: string;
  token?: string;
  userId?: string;
  summarised?: boolean;
  onSaved?: () => void;
};

export default function CallSummary({
  callId,
  token,
  userId,
  summarised = false,
  onSaved,
}: Props) {
  const ui = useUI();
  const [createCallNote] = useMutation(addCallNote);
  const [createTask] = useMutation(addTask);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState('');
  const [noteSaved, setNoteSaved] = useState(false);
  const [tasks, setTasks] = useState<SuggestedTask[]>([]);
  const [created, setCreated] = useState<Record<number, boolean>>({});

  const saveNote = async (content: string) => {
    await createCallNote({
      variables: {
        data: {
          call: callId,
          noteByNote: {
            data: {
              content,
              title: 'Call summary',
            },
          },
        },
      },
      refetchQueries: [
        {
          query: getCallById,
          variables: { id: callId },
        },
      ],
    });
    setNoteSaved(true);
    onSaved?.();
  };

  const summarise = async () => {
    if (!token || busy) return;
    setBusy(true);
    try {
      const response = await fetch('/api/calls/summarise', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ callId }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body?.error || 'The call could not be summarised.');
      }

      const nextSummary = String(body.summary || '').trim();
      const nextTasks = Array.isArray(body.tasks) ? body.tasks : [];
      setSummary(nextSummary);
      setTasks(nextTasks);
      setCreated({});
      setNoteSaved(false);
      await saveNote(nextSummary);
      ui.setToast(true, {
        type: 'success',
        title: 'Call summary added',
        message: nextTasks.length
          ? 'Review the suggested tasks before creating them.'
          : 'The note is on this call.',
      });
    } catch (error) {
      ui.setToast(true, {
        type: 'error',
        title: 'Could not summarise this call',
        message: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setBusy(false);
    }
  };

  const addSuggestedTask = async (task: SuggestedTask, index: number) => {
    if (!userId || created[index]) return;
    const due = new Date();
    due.setDate(due.getDate() + 1);
    due.setHours(9, 0, 0, 0);

    try {
      await createTask({
        variables: {
          data: {
            title: task.title,
            content: task.detail,
            due,
            status: 'TODO',
            user_id: userId,
            priority: 2,
          },
        },
      });
      setCreated((current) => ({ ...current, [index]: true }));
      ui.setToast(true, {
        type: 'success',
        title: 'Task created',
        message: task.title,
      });
    } catch (error) {
      ui.setToast(true, {
        type: 'error',
        title: 'Could not create the task',
        message: error instanceof Error ? error.message : 'Please try again.',
      });
    }
  };

  const hasSummary = summarised || noteSaved;

  if (hasSummary && tasks.length === 0) return null;

  return (
    <div className="space-y-3">
      {hasSummary ? null : (
        <button
          type="button"
          onClick={summarise}
          disabled={!token || busy}
          className="btn-outline !w-auto px-3"
        >
          {busy ? 'Summarising...' : 'Summarise call'}
        </button>
      )}
      {summary && !noteSaved ? (
        <button
          type="button"
          onClick={() => saveNote(summary)}
          className="btn-secondary !w-auto px-3"
        >
          Save summary note
        </button>
      ) : null}
      {tasks.length > 0 ? (
        <div className="rounded border border-gray-300 p-3">
          <p className="text-sm font-semibold text-gray-900">Suggested tasks</p>
          <ul className="mt-2 space-y-3">
            {tasks.map((task, index) => (
              <li key={`${task.title}-${index}`}>
                <p className="text-sm font-medium text-gray-900">
                  {task.title}
                </p>
                {task.detail ? (
                  <p className="mt-1 text-sm font-light text-gray-600">
                    {task.detail}
                  </p>
                ) : null}
                <button
                  type="button"
                  disabled={!userId || created[index]}
                  onClick={() => addSuggestedTask(task, index)}
                  className="mt-2 text-sm font-medium text-purple-700 hover:text-purple-800 disabled:text-gray-400"
                >
                  {created[index] ? 'Task created' : 'Create task'}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
